import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Command as CommandPrimitive } from "cmdk";
import { Loader2, Search } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";

import { Command, CommandList } from "@/components/ui/command";

import {
  QuickLinkItems,
  RecentSearches,
  SearchBody,
  SearchHint,
} from "./SearchResults";
import { QUICK_LINKS, recentValue } from "./searchLinks";
import { SEARCH_RESOURCES } from "./searchModel";
import type { SearchController } from "./useSearchController";

/**
 * cmdk value of the first row shown: the first result, in group order, or in
 * the empty state the first recent search, else the first quick link.
 */
const firstRowValue = (search: SearchController): string => {
  if (search.isTooShort) {
    const [recent] = search.recents.recent;
    return recent ? recentValue(recent) : `quick.${QUICK_LINKS[0].name}`;
  }
  for (const { name } of SEARCH_RESOURCES) {
    const first = search.linkedResults.find(
      ({ result }) => result.resource === name,
    );
    if (first) return `${name}.${first.result.record_id}`;
  }
  return "";
};

const Key = ({ children }: { children: string }) => (
  <kbd className="search-d__kbd">{children}</kbd>
);

/**
 * Desktop Cmd/Ctrl+K command palette: pinned near the top so it grows
 * downwards as results arrive, arrow keys + Enter through cmdk, and a footer
 * with the keys. Knowvio styling from the --desk-* tokens (styles/search.css).
 */
export const DesktopSearchPalette = ({
  open,
  search,
}: {
  open: boolean;
  search: SearchController;
}) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  // Controlled: cmdk loses its selection when the rows it had selected
  // unmount (empty state -> results), which would leave Enter doing nothing.
  const [selected, setSelected] = useState("");
  const firstValue = firstRowValue(search);
  useEffect(() => {
    if (firstValue) setSelected(firstValue);
  }, [firstValue]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          search.rememberQuery();
          search.close();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="search-d-backdrop" />
        <DialogPrimitive.Popup initialFocus={inputRef} className="search-d">
          <DialogPrimitive.Title className="sr-only">
            {translate("crm.search.title")}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {translate("crm.search.placeholder")}
          </DialogPrimitive.Description>
          <Command
            shouldFilter={false}
            loop
            value={selected}
            onValueChange={setSelected}
            label={translate("crm.search.title")}
            className="search-d__command"
          >
            <div className="search-d__field">
              <Search
                aria-hidden
                className="size-[18px] shrink-0"
                strokeWidth={2}
              />
              <CommandPrimitive.Input
                ref={inputRef}
                value={search.query}
                onValueChange={search.changeQuery}
                placeholder={translate("crm.search.placeholder")}
                className="search-d__input"
              />
              {search.isFetching ? (
                <Loader2
                  role="status"
                  aria-label={translate("crm.search.searching")}
                  className="search-spinner size-4 shrink-0"
                />
              ) : null}
              <Key>esc</Key>
            </div>
            <CommandList className="search-d__list">
              <div className="search-d__body">
                <SearchBody
                  search={search}
                  emptyState={
                    <>
                      <RecentSearches search={search} />
                      <QuickLinkItems search={search} />
                      <SearchHint />
                    </>
                  }
                />
              </div>
            </CommandList>
            <div className="search-d__footer" aria-hidden>
              <span>
                <Key>↑</Key>
                <Key>↓</Key>
                {translate("crm.search.keys.navigate")}
              </span>
              <span>
                <Key>↵</Key>
                {translate("crm.search.keys.open")}
              </span>
              <span>
                <Key>esc</Key>
                {translate("crm.search.keys.close")}
              </span>
            </div>
          </Command>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
